import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useGetAccountAlerts } from '@/hooks/useGetAccountAlerts';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { Asset, ResourceType, Risk, Technology } from '@/types';
import { useMutation } from '@/utils/api';
import { getResourceType } from '@/utils/resourceType.util';

interface RemoveCommentParams {
  key: string;
  cpe?: string;
  remove: number; // 0-based index in history array
}

export function useRemoveComment() {
  const axios = useAxios();

  return useMutation<Asset | Risk, Error, RemoveCommentParams>({
    defaultErrorMessage: 'Failed to remove note',
    mutationFn: async ({ key, cpe, remove }) => {
      const resourceType = getResourceType(key);
      const promise = axios.put(`/${resourceType}`, {
        key,
        remove,
        ...(cpe && { cpe }),
      });

      toast.promise(promise, {
        loading: 'Removing note...',
        success: 'Note removed successfully',
        error: 'Failed to remove note',
      });

      const { data } = await promise;

      // Invalidate the appropriate query
      if (resourceType === ResourceType.Risk) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(
            JSON.stringify(key ?? ''),
            undefined,
            undefined
          ),
        });
        // Use predicate-based invalidation to catch ALL risk queries
        // This ensures both table and drawer views refetch
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'risk';
          },
          refetchType: 'active',
        });
      } else if (resourceType === ResourceType.Asset) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy('asset'),
        });
      } else if (resourceType === ResourceType.Technology) {
        // Use predicate-based invalidation to catch ALL technology queries
        // This ensures both table and drawer views refetch
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
          refetchType: 'active',
        });
      }

      return data;
    },
  });
}

interface AddCommentParams {
  key: string;
  name: string;
  comment: string;
  status?: string;
  cpe?: string;
  showSnackbar?: boolean; // Default true for backward compatibility
}

export const useAddComment = () => {
  const axios = useAxios();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<Asset | Risk | Technology, Error, AddCommentParams>({
    defaultErrorMessage: 'Failed to add comment',
    mutationFn: async params => {
      const { key, name, comment, status, cpe, showSnackbar = true } = params;
      const resourceType = getResourceType(key);

      const promise = axios.put(`/${resourceType}`, {
        key,
        name,
        comment,
        ...(status && { status }),
        ...(cpe && { cpe }),
      });

      // Only show toast if showSnackbar is not explicitly false
      if (showSnackbar) {
        toast.promise(promise, {
          loading: 'Adding note...',
          success: 'Note added successfully',
          error: 'Failed to add note',
        });
      }

      const { data } = await promise;

      invalidateAlerts();
      if (resourceType === ResourceType.Risk) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(
            JSON.stringify(key ?? ''),
            undefined,
            undefined
          ),
        });
        // Use predicate-based invalidation to catch ALL risk queries
        // This ensures both table and drawer views refetch
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'risk';
          },
          refetchType: 'active',
        });
      } else if (resourceType === ResourceType.Asset) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy('asset'),
        });
      } else if (resourceType === ResourceType.Technology) {
        // Use predicate-based invalidation to catch ALL technology queries
        // This ensures both table and drawer views refetch
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
          refetchType: 'active',
        });
      }

      return data;
    },
  });
};
